import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
} from '@chakra-ui/react'
import React, { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

interface Props {
  isOpen: boolean
  onClose(): void
  // Le nom à retaper. C'est lui qui débloque le bouton, rien d'autre.
  expectedName: string
  title: string
  description: React.ReactNode
  confirmLabel: string
  onConfirm(): Promise<void>
}

/**
 * Confirmation par saisie du nom, comme GitHub ou GitLab.
 *
 * 🔑 Un simple « Êtes-vous sûr ? » se clique par réflexe : la main est déjà en
 * route vers le bouton avant que l'œil ait lu la phrase. Retaper le nom force à
 * regarder QUELLE organisation est visée — c'est la seule protection qui vaut
 * quelque chose quand l'action est irréversible et cascade sur des années de
 * données (ADM-D4).
 */
export default function AdminDangerConfirm({
  isOpen,
  onClose,
  expectedName,
  title,
  description,
  confirmLabel,
  onConfirm,
}: Props) {
  const { t } = useTranslation()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [typedName, setTypedName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Comparaison sur le nom exact, espaces de bord mis à part : on protège
  // contre le clic distrait, pas contre une majuscule mal placée par un copier-coller.
  const isConfirmed = typedName.trim() === expectedName.trim()

  const handleClose = () => {
    setTypedName('')
    onClose()
  }

  const handleConfirm = async () => {
    if (!isConfirmed) return
    setLoading(true)
    setError(null)
    try {
      await onConfirm()
      setTypedName('')
      onClose()
    } catch (e: any) {
      // La fenêtre RESTE ouverte, avec la raison. Sans ce message, un échec se
      // confond avec un clic non pris : le bouton redevient simplement actif et
      // on recommence indéfiniment la même action refusée.
      setError(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={handleClose}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{title}</AlertDialogHeader>

          <AlertDialogBody>
            {description}

            <FormControl mt={5}>
              <FormLabel fontSize="sm">
                <Trans
                  i18nKey="SuperAdmin.orgs.danger.typeName"
                  values={{ name: expectedName }}
                  components={{ b: <strong /> }}
                />
              </FormLabel>
              <Input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={expectedName}
                autoComplete="off"
              />
            </FormControl>

            {typedName !== '' && !isConfirmed && (
              <Text mt={2} fontSize="sm" color="orange.500">
                {t('SuperAdmin.orgs.danger.nameMismatch')}
              </Text>
            )}

            {error && (
              <Alert status="error" mt={4} fontSize="sm">
                <AlertIcon />
                {error}
              </Alert>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              isDisabled={!isConfirmed}
              isLoading={loading}
              onClick={handleConfirm}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
